import type {
  ActorRef,
  ApprovalPolicy,
  ChannelSummary,
  InsightActionCounts,
  InsightActionGroup,
  InsightActionGroupSummary,
  InsightActionPreview,
  InsightActionRow,
  RowEligibility,
} from '../models/insightAction.types'
import type {
  ActorRefDto,
  ApprovalPolicyDto,
  ChannelSummaryDto,
  InsightActionCountsDto,
  InsightActionPreviewDto,
  PreviewActionGroupDto,
  PreviewRowDto,
  PreviewSummaryDto,
  RowEligibilityDto,
} from '../models/insightAction.wire'

export function adaptCounts(dto: InsightActionCountsDto | undefined): InsightActionCounts {
  return {
    succeeded: dto?.succeeded ?? 0,
    failed: dto?.failed ?? 0,
    awaitingApproval: dto?.awaiting_approval ?? 0,
    skipped: dto?.skipped ?? 0,
  }
}

export function adaptActor(dto: ActorRefDto | null | undefined): ActorRef | null {
  if (!dto) return null
  return { id: dto.id, displayName: dto.display_name }
}

function adaptApproval(dto: ApprovalPolicyDto | null | undefined): ApprovalPolicy | null {
  if (!dto) return null
  return {
    requiredRole: dto.required_role,
    requiresSecondPerson: dto.requires_second_person,
    approvalRequired: dto.approval_required,
    source: dto.source,
  }
}

function adaptEligibility(dto: RowEligibilityDto | null | undefined): RowEligibility | null {
  if (!dto) return null
  return {
    state: dto.state,
    reasonCode: dto.reason_code,
    message: dto.message ?? '',
    retryable: Boolean(dto.retryable),
    releasedAt: dto.released_at ?? null,
  }
}

export function adaptRow(dto: PreviewRowDto): InsightActionRow {
  return {
    rowKey: dto.row_key,
    actionable: Boolean(dto.actionable),
    channelKey: dto.channel_key ?? null,
    reasonCode: dto.reason_code ?? null,
    campaignLabel: dto.campaign_label ?? null,
    eligibility: adaptEligibility(dto.eligibility),
    summary: dto.summary_tr ?? '',
    columns: dto.columns ?? {},
  }
}

function adaptChannel([key, dto]: [string, ChannelSummaryDto]): ChannelSummary {
  return {
    key,
    total: dto.total ?? 0,
    actionable: dto.actionable ?? 0,
    blocked: dto.blocked ?? 0,
    reason: dto.reason ?? null,
  }
}

function adaptSummary(dto: PreviewSummaryDto): InsightActionGroupSummary {
  return {
    total: dto.total ?? 0,
    actionable: dto.actionable ?? 0,
    blocked: dto.blocked ?? 0,
    rowLimitTruncated: Boolean(dto.row_limit_truncated),
    byChannel: Object.entries(dto.by_channel ?? {}).map((entry) => adaptChannel(entry)),
  }
}

export function adaptGroup(dto: PreviewActionGroupDto): InsightActionGroup {
  return {
    key: dto.key,
    label: dto.label,
    tabKey: dto.tab_key,
    approval: adaptApproval(dto.approval),
    revertible: Boolean(dto.revertible),
    eligibilityState: dto.eligibility_state ?? 'ready',
    summary: adaptSummary(dto.summary),
    rows: (dto.rows ?? []).map((row) => adaptRow(row)),
  }
}

export function adaptPreview(dto: InsightActionPreviewDto): InsightActionPreview {
  return {
    insightId: dto.insight_id,
    dataKey: dto.data_key,
    configEnum: dto.config_enum,
    groups: (dto.action_groups ?? []).map((group) => adaptGroup(group)),
    activeRun: dto.active_run
      ? {
          runId: dto.active_run.run_id,
          status: dto.active_run.status,
          totalItems: dto.active_run.total_items,
          counts: adaptCounts(dto.active_run.counts),
        }
      : null,
    executionEnabled: dto.execution_enabled !== false,
    runCount: dto.run_count ?? 0,
  }
}
